import { FileType } from "constants/common";
import {
  handlePrintFile,
  printDocFile,
  PrintOptions,
} from "helpers/printHelpers";
import { useCallback, useState } from "react";

export type PrintDocumentOptions = Pick<
  PrintOptions,
  "openPdfInNewTabForPrint"
> & {
  /** When {@link FileType.DOCX}, render with docx-preview then print; PDF uses iframe / new-tab flow. */
  fileType?: FileType;
};

/**
 * Shared print flow: DOCX via {@link printDocFile}; PDF via fetch → blob → iframe print, with loading target id and toast on failure.
 */
export function usePrintDocument(
  showError: (title: string, message: string) => void,
) {
  const [printingTarget, setPrintingTarget] = useState<string | null>(null);

  const printDocument = useCallback(
    (
      url: string | undefined,
      fileName: string,
      target: string,
      printFileOptions?: PrintDocumentOptions,
    ) => {
      if (!url?.trim()) return;
      const printFn =
        printFileOptions?.fileType === FileType.DOCX
          ? printDocFile
          : handlePrintFile;
      printFn(url, fileName, {
        openPdfInNewTabForPrint: printFileOptions?.openPdfInNewTabForPrint,
        onStart: () => setPrintingTarget(target),
        onComplete: () => setPrintingTarget(null),
        onError: () => {
          setPrintingTarget(null);
          showError(
            "Print Error",
            "Failed to print document. Opening in new tab instead.",
          );
        },
      });
    },
    [showError],
  );

  return { printingTarget, printDocument, setPrintingTarget };
}
